import React, { useContext, useState, useEffect, useRef } from "react";
import _ from "lodash";
import { QueryContent } from "@repoflow/components/content-query";
import { useMutation } from "@apollo/client";
import * as Loading from "@repoflow/components/loading";
import { Provider as SchemaProvider } from "@repoflow/form-schema/schema-config";
import { useValueState } from "@repoflow/form-schema/section-state";
import * as NotifyUtils from "@repoflow/form-schema/notify";
import { useUnsavedChanges, isWithin } from "@repoflow/components/unsaved-changes";

export const Context = React.createContext(null);

export const useSectionContext = () => {
  return useContext(Context);
};

export const Button = ({
  enabledClass = "btn btn-success",
  disabledClass = "btn btn-secondary",
  onClick,
  children,
  type = "button",
  loading: inLoading,
  // For constraints the schema cannot express — a field that must equal
  // another one, say. Mirrors Action's prop of the same name.
  disabled,
}) => {
  const current = useContext(Context);

  const { state, loading } = current;
  const { isValid, isEqual } = state;

  const isLoading = loading || inLoading;
  const allowed = !isEqual() && isValid() && !isLoading && !disabled;

  return (
    <button
      type={type}
      className={allowed ? enabledClass : disabledClass}
      disabled={!allowed}
      onClick={(e) => {
        onClick ? onClick(current) : current.mutate();
      }}
    >
      {isLoading ? <Loading.Icon /> : children}
    </button>
  );
};

// WHY EVERY ROUTE CALLS ITS RENDER PROP INSTEAD OF RENDERING IT
//
// Each route in each app wraps this in a `LocalProvider` that takes the rest of
// the screen as `children` and hands it `base`, `basepath` and the params. The
// obvious way to deliver that is to name the prop `Children` and render
// `<Children base={base} ... />`.
//
// That is a remount on every render, and it is silent.
//
// The children arrive as an arrow written inline in JSX — `{({ basepath }) =>
// (<Switch>…)}` — so the function is a NEW VALUE each time the route renders.
// Rendered as an element, that value is the component TYPE, and React unmounts
// and rebuilds everything under a type it has not seen before. A location change
// re-renders every descendant of the Router, so this fires constantly.
//
// What it costs is component state, which in this app is every form: a page
// half-edited comes back empty, a tab switch returns to the first tab, and a
// modal open over a form is destroyed with the row being added still in it.
// Nothing throws and nothing is logged — the screen is still there, so it reads
// as a click that did not register.
//
// It was mistaken for the `config` prop's identity for weeks (P139), because a
// new-but-equal config is a common TRIGGER: it re-renders the parent, and a
// parent re-render is what turns an inline component into a remount. The config
// is not the fault. A form under a stable component tree survives a new config
// object with its DOM nodes, its state and its open modal intact — pinned by
// `provider-identity.test.js` in the form-schema repo, which asserts both halves.
//
// So the routes call it: `{Children({ base, basepath, params })}`. There is no
// component boundary, nothing to remount, and the render prop is a plain
// function call — which is what it always was.
//
// One thing that remount WAS doing is now asked for explicitly: every entity
// route puts a `key` on this Provider, so moving from one page to another still
// resets the forms below it. See any `routes/**/<entity>/index.js`.
export const Provider = (props) => {
  const {
    children,

    schema,
    base,
    basepath,

    attributes,

    state,
    target,
  } = props;

  const current = useContext(Context);

  const sectionContext = {
    ...current,
    parent: current,
    schema: schema ? schema : current ? current.schema : null,
    children,
    base,
    basepath,
    attributes,
    state: state || (current ? current.state : null),
    target: target || (current ? current.target : null),
  };

  return (
    <SchemaProvider schema={schema} widgets={[]} config={null}>
      <Context.Provider value={sectionContext}>{children}</Context.Provider>
    </SchemaProvider>
  );
};

export const Query = ({ query, variables, datapath, children, context, fetchPolicy }) => {
  return (
    <QueryContent
      query={query}
      variables={variables}
      // See the note on the prop in components/content-query: only a read whose
      // result is a concurrency token needs this, and only tablews has one.
      fetchPolicy={fetchPolicy}
      context={{ query, variables, datapath, children, context }}
    >
      {QueryHandler}
    </QueryContent>
  );
};

const QueryHandler = ({
  account,
  context,
  context: { children: Children },
  refetch,
}) => {
  const current = useContext(Context);
  const values = _.get(account, context.datapath);

  const sectionContext = {
    ...current,
    query: context,
    account,
    values,
    // ON THE CONTEXT AS WELL AS THE PROP, and the omission was a silent one.
    //
    // `refetch` re-runs the query this section is built from, and it reached the
    // immediate child as a prop only. Anything further down asked
    // `useSectionContext()` for it, got `undefined`, and — because every caller
    // guards with `refetch && refetch()`, which is what you write when you are
    // not sure — did nothing at all, silently, forever.
    //
    // Measured in tablews: a table's Sync button polls the row while the sync is
    // running, and the poll never armed; the handler recorded its result in
    // under a second and the screen still showed nothing 45 seconds later. Its
    // rows editor has the same call after a save, to pick up the new S3 etag,
    // and that had never run either.
    //
    // Additive: the prop is still passed, so nothing that reads it changes.
    refetch,
  };

  return (
    <Context.Provider value={sectionContext}>
      <Children
        {...sectionContext}
        context={context.context}
        refetch={refetch}
      />
    </Context.Provider>
  );
};

// WHAT `guard` IS
//
// Every Mutation with a `target` warns before its screen goes away with edits
// nobody saved — see @repoflow/components/unsaved-changes for the three ways a
// screen goes away and why one hook covers them. Dirty is what the Save button
// already reads: `!state.isEqual()`, the values against the seed. A Mutation
// with no target (the Remove screens, a Verify button) has no form and never
// counts as dirty.
//
// `guard={false}` is for a Mutation that IS a form but whose half-typed state
// is nobody's work: the sign-in, sign-up and password screens. Leaving one of
// those unfinished is the normal way to leave it.
//
// `within` is the one screen whose form outlives a navigation. The page editor
// puts an element on a route UNDER its own — `/edit/element/:index` — and the
// editor, this Mutation included, stays mounted while the element screen is
// open and when Back returns from it. Without this the guard reads that Back as
// leaving, and a browser that dismisses the prompt (Playwright does, by
// default) never gets back to the list. So a Mutation may name the pathname it
// lives under, and a destination at or beneath it is not a departure. Nothing
// else nests a route under a form today; a screen that starts to must say so.
//
// The `completed` flag is the timing case. A save lands and its `onCompleted`
// navigates — `history.replace(base)` on every create and most edits — from
// inside Apollo's callback, synchronously, before React has re-rendered; at
// that instant the values still differ from the seed and a plain `!isEqual()`
// would prompt on the redirect the save itself asked for. So completion is
// noted first and the next write clears it: a screen that stays put after a
// save (the site, the workflow editor) is clean until somebody types again.
export const Mutation = ({
  mutation,
  update,
  onMutate,
  onCompleted,
  onError,
  target,
  initial,
  children: Children,
  context,
  refetch = [],
  guard = true,
  within,
}) => {
  const [trigger, triggerMutation] = useState(false);
  const current = useContext(Context);
  const initialVal = initial ? initial : current.values;

  const valueState = useValueState({
    target,
    initial: initialVal,
    context: current,
  });

  const completed = useRef(false);
  const state = {
    ...valueState,
    setValues: (values) => {
      completed.current = false;
      valueState.setValues(values);
    },
  };

  useUnsavedChanges(
    (location) =>
      guard &&
      Boolean(target) &&
      !completed.current &&
      !(location && isWithin(location.pathname, within)) &&
      !valueState.isEqual()
  );

  const sectionContext = {
    ...current,
    target,
    initial: initialVal,
    state,
    mutate: () => {
      triggerMutation(true);
    },
  };

  const [handler, { loading, error }] = useMutation(mutation, {
    refetchQueries: refetch,
    update,
    onCompleted: (data) => {
      completed.current = true;
      onCompleted && onCompleted(data, sectionContext);
    },
    onError: (error) => {
      onError
        ? onError(error, sectionContext)
        : NotifyUtils.error(error.toString());
    },
  });

  sectionContext.handler = handler;
  sectionContext.loading = loading;
  sectionContext.error = error;

  useEffect(() => {
    if (trigger) {
      onMutate && onMutate(sectionContext);
      triggerMutation(false);
    }
  }, [trigger]);

  return (
    <Context.Provider value={sectionContext}>
      <Children {...sectionContext} context={context} />
    </Context.Provider>
  );
};

export const Action = ({
  disabled,
  refetch,
  update,
  mutation,
  enabledClass = "btn btn-success",
  disabledClass = "btn btn-secondary",
  onCompleted,
  onError,
  children,
  variables,
}) => {
  const current = useContext(Context);

  const { loading: busy } = current;

  const [handler, { loading, error }] = useMutation(mutation, {
    refetchQueries: refetch,
    update,
    onCompleted: (data) => onCompleted && onCompleted(data, current),
    onError: (error) => onError && onError(error, current),
  });

  const isLoading = loading;
  const allowed = !isLoading && !busy && !disabled;
  return (
    <button
      className={allowed ? enabledClass : disabledClass}
      disabled={!allowed}
      onClick={(e) => {
        handler({ variables });
      }}
    >
      {isLoading ? <Loading.Icon /> : children}
    </button>
  );
};
